import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Schedule as ScheduleIcon,
    Security as SecurityIcon,
    VpnKey as VpnKeyIcon,
    Close as CloseIcon,
    ExitToApp as ExitToAppIcon
} from '@mui/icons-material';
import { useAuth0 } from '../context/Auth0Context';

interface UserProfileDialogProps {
    open: boolean;
    onClose: () => void;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ open, onClose }) => {
    const { user, logout, isLoading } = useAuth0();
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const handleChangePassword = () => {
        setIsChangingPassword(true);

        // Redirect to Auth0's change password page
        const auth0Domain = 'dev-l00eyle3qdyqapdy.us.auth0.com';
        const changePasswordUrl = `https://${auth0Domain}/u/reset-password/request`;

        // Open in new tab
        window.open(changePasswordUrl, '_blank', 'noopener,noreferrer');

        // Reset loading state after a delay
        setTimeout(() => {
            setIsChangingPassword(false);
        }, 2000);
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return 'Unknown';
        }
    };

    if (isLoading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="user-profile-dialog-title"
            aria-describedby="user-profile-dialog-description"
            disableAutoFocus={false}
            disableEnforceFocus={false}
            disableRestoreFocus={false}
        >
            <DialogTitle id="user-profile-dialog-title">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">User Profile</Typography>
                    <IconButton onClick={onClose} size="small" aria-label="Close dialog">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <div id="user-profile-dialog-description">
                    {user && (
                        <Box>
                            {/* User Avatar and Basic Info */}
                            <Box display="flex" alignItems="center" mb={3}>
                                <Avatar
                                    src={user.picture}
                                    alt={user.name}
                                    sx={{ width: 80, height: 80, mr: 3 }}
                                >
                                    {user.name?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" gutterBottom>
                                        {user.name || 'User'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {user.email}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Authenticated via Auth0
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* User Details */}
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <PersonIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Full Name"
                                        secondary={user.name || 'Not provided'}
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemIcon>
                                        <EmailIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Email Address"
                                        secondary={user.email || 'Not provided'}
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemIcon>
                                        <ScheduleIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Last Login"
                                        secondary={user.updated_at ? formatDate(user.updated_at) : 'Unknown'}
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemIcon>
                                        <SecurityIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Email Verified"
                                        secondary={user.email_verified ? 'Yes' : 'No'}
                                    />
                                </ListItem>
                            </List>

                            <Divider sx={{ my: 2 }} />

                            {/* Security Actions */}
                            <Typography variant="h6" gutterBottom>
                                Security Settings
                            </Typography>

                            <Box display="flex" flexDirection="column" gap={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={isChangingPassword ? <CircularProgress size={20} /> : <VpnKeyIcon />}
                                    onClick={handleChangePassword}
                                    disabled={isChangingPassword}
                                    fullWidth
                                >
                                    {isChangingPassword ? 'Opening Password Reset...' : 'Change Password'}
                                </Button>

                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Password changes are handled securely through Auth0.
                                    You'll receive an email with reset instructions.
                                </Alert>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* Logout Section */}
                            <Box display="flex" justifyContent="center">
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<ExitToAppIcon />}
                                    onClick={() => {
                                        onClose();
                                        logout(true);
                                    }}
                                    sx={{ px: 4 }}
                                >
                                    Logout
                                </Button>
                            </Box>
                        </Box>
                    )}
                </div>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserProfileDialog;
